/**
 * Header — slim top bar: the current page title and the role-view selector on the
 * left, and a mock-mode badge on the right. No person names or emails are shown.
 */

import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../lib/config';
import { ssoEnabled } from '../../lib/sso';
import { RoleSwitcher } from '../RoleSwitcher';

const TITLES: Record<string, string> = {
  '/chat': 'Chat',
  '/retrieval': 'Permission-Aware Retrieval',
  '/knowledge': 'Knowledge Browser',
  '/audit': 'Audit Trail',
  '/dashboard': 'Dashboard',
  '/evals': 'Evaluations',
  '/tools': 'Tool Registry',
  '/connectors': 'Connectors',
  '/agents': 'Agent Builder',
  '/agents/runs': 'Run History',
};

function titleFor(pathname: string): string {
  if (TITLES[pathname]) return TITLES[pathname];
  const base = '/' + (pathname.split('/').filter(Boolean)[0] ?? '');
  return TITLES[base] ?? 'Trident AI';
}

export function Header(): ReactNode {
  const { pathname } = useLocation();
  const { user } = useAuth();

  return (
    <header className="h-16 bg-white border-b border-steel-200 flex items-center justify-between px-6 print:hidden">
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold text-navy-900">{titleFor(pathname)}</h1>
        {/* Role switching is demo machinery: /auth/assume-role mints demo
            tokens, which SSO-mode verification rejects — and impersonation
            has no place in prod. Demo builds keep it. */}
        {user && !ssoEnabled() && <RoleSwitcher />}
      </div>
      {config.mockMode && (
        <span className="px-2 py-0.5 rounded-full bg-gold-100 text-gold-800 text-xs font-medium">
          Mock Mode
        </span>
      )}
    </header>
  );
}

export default Header;
